import { useEffect, useRef } from "react";
import { getStored, setStored } from "@/lib/storage";
import { PREF } from "@/lib/prefs";

const SCROLLER = "desk-main-scroll";
const KEEP = 30;
/** How long a restore keeps retrying while a list loads. */
const RESTORE_MS = 1500;

/**
 * Remembers the main scroll offset per screen and restores it on return (and on launch with
 * "Open to: Last view"). Screens never visited start at the top, as before.
 */
export function useScrollRestoration(path: string) {
  const positions = useRef<Record<string, number>>(getStored(PREF.scroll, {}));
  const current = useRef(path);
  const restoring = useRef(false);

  // Record offsets as the user scrolls (skipped while a restore is still settling).
  useEffect(() => {
    let frame = 0;
    let saveTimer: ReturnType<typeof setTimeout> | null = null;
    const persist = () => {
      const entries = Object.entries(positions.current).slice(-KEEP);
      positions.current = Object.fromEntries(entries);
      setStored(PREF.scroll, positions.current);
    };
    const onScroll = (e: Event) => {
      const el = e.target as HTMLElement;
      if (el.id !== SCROLLER || restoring.current || frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const { [current.current]: _, ...rest } = positions.current;
        positions.current = el.scrollTop > 0 ? { ...rest, [current.current]: el.scrollTop } : rest;
        if (saveTimer) clearTimeout(saveTimer);
        saveTimer = setTimeout(persist, 400);
      });
    };
    // Capture: the scroller can be replaced by a concept's shell without this effect rerunning.
    document.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("scroll", onScroll, true);
      cancelAnimationFrame(frame);
      if (saveTimer) clearTimeout(saveTimer);
      persist();
    };
  }, []);

  // Restore on screen change, retrying until the list is tall enough or the user scrolls.
  useEffect(() => {
    current.current = path;
    const target = positions.current[path] ?? 0;
    const el = document.getElementById(SCROLLER);
    if (!el) return;
    el.scrollTo({ top: target });
    if (!target || el.scrollTop >= target - 1) return;
    restoring.current = true;
    const started = performance.now();
    let frame = 0;
    const stop = () => {
      restoring.current = false;
      cancelAnimationFrame(frame);
      el.removeEventListener("wheel", stop);
      el.removeEventListener("touchstart", stop);
      window.removeEventListener("keydown", stop);
    };
    const tick = () => {
      el.scrollTo({ top: target });
      if (el.scrollTop >= target - 1 || performance.now() - started > RESTORE_MS) return stop();
      frame = requestAnimationFrame(tick);
    };
    el.addEventListener("wheel", stop, { passive: true });
    el.addEventListener("touchstart", stop, { passive: true });
    window.addEventListener("keydown", stop);
    frame = requestAnimationFrame(tick);
    return stop;
  }, [path]);
}
